import { useState } from 'react'
import { Button, Form, Input, Select, Tag, InputNumber, App } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rejectionNotesApi } from '@/features/procurement/api'
import { plantsApi } from '@/features/masters/plants/api'
import { suppliersApi } from '@/features/gate/api'
import type { RejectionNote } from '@/shared/types/procurement'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'

const RTV_COLORS: Record<string, string> = {
  PENDING: 'orange', RETURNED: 'green', REPLACED: 'blue', CREDITED: 'purple',
}

export default function RejectionNotePage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()

  const { data, isLoading } = useQuery({
    queryKey: ['rejection-notes', plantFilter],
    queryFn: () => rejectionNotesApi.list({ plant_id: plantFilter }),
  })
  const { data: plantsData } = useQuery({ queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 500 }) })
  const { data: suppliers } = useQuery({ queryKey: ['suppliers-all'], queryFn: () => suppliersApi.list({ limit: 500 }) })

  const plantOptions = plantsData?.items.map(p => ({ label: p.name, value: p.id })) ?? []
  const supplierOptions = suppliers?.items?.map((s: any) => ({ label: s.supplier_name, value: s.id })) ?? []

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => rejectionNotesApi.create(values),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rejection-notes'] }); message.success('Rejection Note created'); setDrawerOpen(false) },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      rejectionNotesApi.update(id, { rtv_status: status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rejection-notes'] }); message.success('Status updated') },
    onError: () => message.error('Update failed'),
  })

  const columns = [
    { title: 'RN No', dataIndex: 'rn_no', key: 'rn_no', width: 120 },
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name' },
    { title: 'Item', dataIndex: 'item_name', key: 'item_name' },
    { title: 'Lot No', dataIndex: 'lot_no', key: 'lot_no', width: 100 },
    { title: 'Rej Qty (Kg)', dataIndex: 'quantity_rejected_kg', key: 'quantity_rejected_kg', width: 120 },
    { title: 'Reason', dataIndex: 'rejection_reason', key: 'rejection_reason', ellipsis: true },
    {
      title: 'RTV Status', dataIndex: 'rtv_status', key: 'rtv_status', width: 120,
      render: (v: string) => <Tag color={RTV_COLORS[v]}>{v}</Tag>,
    },
    {
      title: '', key: 'action', width: 120,
      render: (_: unknown, r: RejectionNote) =>
        r.rtv_status === 'PENDING' ? (
          <Select
            size="small"
            value={r.rtv_status}
            onChange={(v) => updateMutation.mutate({ id: r.id, status: v })}
            options={['RETURNED', 'REPLACED', 'CREDITED'].map(s => ({ label: s, value: s }))}
            style={{ width: 110 }}
          />
        ) : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Rejection Notes / RTV"
        subtitle="Record material rejections and return-to-vendor tracking"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setDrawerOpen(true) }}>New RN</Button>}
      />

      <div style={{ marginBottom: 16 }}>
        <Select allowClear placeholder="Plant" options={plantOptions} onChange={v => setPlantFilter(v)} style={{ width: 180 }} />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer
        title="New Rejection Note"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => createMutation.mutate(v))}
        loading={createMutation.isPending}
        form={form}
      >
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Form.Item name="supplier_id" label="Supplier" rules={[{ required: true }]}>
          <Select options={supplierOptions} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="rejection_reason" label="Rejection Reason" rules={[{ required: true }]}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="quantity_rejected_kg" label="Quantity Rejected (Kg)" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} min={0.001} />
        </Form.Item>
        <Form.Item name="lot_no" label="Lot No">
          <Input />
        </Form.Item>
        <Form.Item name="grn_id" label="GRN ID (optional)">
          <Input placeholder="UUID of GRN" />
        </Form.Item>
      </FormDrawer>
    </div>
  )
}
